import * as StateLib from "@/Utils/StateLib";
import { Button, Menu } from "@theorgchartdev/storybook";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useViewManagerContext } from "@/providers/ViewManagerProvider";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import LargeOptionContent from "../../../Blocks/Composites/LargeOptionContent/LargeOptionContent";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import {
  enumUISubModes,
  enumUIModes,
  kAdminUser,
  kRWUser,
  enumFeaturesList,
  enumBrand,
  DISPLAY_MODES,
  SEARCH_PARAM_DISPLAY_KEY
} from "@coreapp/constants";
import { gWhiteLabel, gUpgradeIndicatorIcon } from "@coreapp/whitelabel/brand";
import { isInViewManager } from "@coreapp/utils/isInViewManager";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { IsBackgroundFile } from "@coreapp/utils/IsBackgroundFile";
import { getFeatureAccess } from "@/Utils/Misc";
import CustomChartBuilderModal from "@/appComponents/Blocks/Modals/CustomChartBuilderModal/CustomChartBuilderModal";
import { useTopic } from "@/hooks/useTopic";
import { useRouteState } from "@/hooks/useRouteState";
import { GetWSMgr } from "@/Utils/StateLib";
import { editModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

const EditMenu = props => {
  const { inEditMode, showCustomChartUpgradeGate } = props;
  const nav = useAppNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useViewManagerContext();
  const { chartDocument } = useChartDocumentContext();
  const { handleSideBarModeChange, sideBarExpanded, openSidebar } = useRightSidebarContext();
  const { mode } = useRouteState();
  const showCustomChartBuilderModal = searchParams.get("create") === "true" && mode === "chart";
  const hasBuildStarted = React.useRef(false);

  const {
    builderContextValue: { createCustomChart }
  } = useWSMgrContext();

  const editChart = () => {
    let workspaceMgr = StateLib.GetWSMgr();
    let doc = StateLib.GetDoc();
    if (doc == null) return;
    nav.setMode("edit");
    let eventTracker = workspaceMgr.GetUsageTracker();
    eventTracker.ReportEvent("edit_mode.editor.opened", { chart_type: "custom" });
  };

  const editBaseChart = () => {
    // todo
    let workspaceMgr = StateLib.GetWSMgr();
    let eventTracker = workspaceMgr.GetUsageTracker();
    eventTracker.ReportEvent("edit_mode.editor.opened", { chart_type: "base" });
  };

  const editBackground = () => {
    let workspaceMgr = StateLib.GetWSMgr();
    let doc = StateLib.GetDoc();
    if (doc == null) return;
    workspaceMgr.SwitchSubMode(enumUISubModes.backgroundeditor);
    let eventTracker = workspaceMgr.GetUsageTracker();
    eventTracker.ReportEvent("background_editor.editor.opened");
  };

  const returnToViewing = () => {
    let workspaceMgr = StateLib.GetWSMgr();
    let doc = StateLib.GetDoc();
    let filterMgr = doc?.GetFilterMgr();
    if (isInViewManager()) {
      workspaceMgr.FinishActiveViewEdit();
    } else {
      if (filterMgr) Globals._svrChartMgr.PersistFH(doc);
      workspaceMgr.SetReopenAfterSaving();
      nav.setMode("chart");
    }
  };

  const updateChartView = () => {
    const workspaceMgr = StateLib.GetWSMgr();
    // URL-driven: TabularDisplayProvider's URL-sync maps ?display=viewmanager
    // back to SwitchSubMode so coreapp fSubMode stays aligned.
    setSearchParams(params => {
      params.set(SEARCH_PARAM_DISPLAY_KEY, DISPLAY_MODES.VIEWMANAGER);
      return params;
    });
    let eventTracker = workspaceMgr.GetUsageTracker();
    handleSideBarModeChange("view");
    if (!sideBarExpanded) {
      openSidebar();
    }
    eventTracker.ReportEvent("chart_mode.view.saved");
  };

  const editMenuOptions = () => {
    let options = [];
    let wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) return options;
    let doc = StateLib.GetDoc();
    let isServerChart = doc && doc.IsServerChart();
    let userInfo = wsMgr.GetUserInfo();
    let userRole = userInfo ? userInfo.GetUserRole() : null;
    if (!userRole) return options;
    let isAdmin = userRole === kAdminUser;
    let isRWUser = userRole === kAdminUser || userRole === kRWUser;

    /*let editBaseOption = {};
    editBaseOption.label = getOptionContent(
      "Edit Shared Base Chart",
      "Modify the shared baseline used by your organization.",
      "sitemap"
    );
    editBaseOption.clickHandler = editBaseChart;
    editBaseOption.disabled = true;
    editBaseOption.divider = true;
    editBaseOption.value = 0;*/

    let createCustomOption = {};
    createCustomOption.label = (
      <LargeOptionContent
        label={appstr("Create custom chart")}
        description={appstr(
          "Model, filter, and build your own custom chart off the shared base chart"
        )}
        icon={"diagram-project"}
        iconSize={"large"}
        dataTestId={"edit-menu-create-custom-chart"}
      />
    );
    createCustomOption.clickHandler = () => {
      setSearchParams(params => {
        params.set("create", "true");
        return params;
      });
    };
    createCustomOption.divider = true;
    createCustomOption.value = 1;
    createCustomOption.disabled =
      (doc && doc.IsPlanChart()) || !getFeatureAccess(enumFeaturesList.customChart);

    let editCustomOption = {};
    editCustomOption.label = (
      <LargeOptionContent
        label={appstr("Edit this custom chart")}
        description={appstr("Continue editing this custom chart")}
        icon={"diagram-project"} //Pencil?
        iconSize={"large"}
        dataTestId={"edit-menu-edit-custom-chart"}
      />
    );
    editCustomOption.clickHandler = editChart;
    editCustomOption.divider = true;
    editCustomOption.value = 1;

    let updateViewOption = {};
    updateViewOption.label = (
      <LargeOptionContent
        label={appstr("Edit View")}
        description={appstr("Update the automated rules driving chart presentation.")}
        icon={"eye"}
        iconSize={"large"}
        dataTestId={"edit-menu-update-view"}
      />
    );
    updateViewOption.clickHandler = updateChartView;
    updateViewOption.value = 2;
    const activeViewLibrary = StateLib.GetDoc()?.GetActiveTemplate()?.GetTemplateLibrary();
    updateViewOption.disabled = !activeViewLibrary || activeViewLibrary === "system";

    let canEditBaseChart = isAdmin && isServerChart;
    let canEditViews = userInfo.CanUpdateTemplates();

    if (isRWUser && isServerChart) options.push(createCustomOption);
    else if (isRWUser && !isServerChart) options.push(editCustomOption);
    if (canEditViews) options.push(updateViewOption);

    return options;
  };

  useTopic("noMatchingRecords", doc => {
    if (!hasBuildStarted.current) {
      return;
    }
    // Switch back to base chart in case the filter matches no records.
    const wsMgr = GetWSMgr();
    wsMgr?.SwitchToMasterChart(doc.GetChartID());

    setSearchParams(params => {
      params.set("create", "true");
      return params;
    });
  });

  useTopic("chartDocumentLoaded", () => {
    if (hasBuildStarted.current) {
      hasBuildStarted.current = false;
      setSearchParams(
        params => {
          params.set("mode", "edit");
          return params;
        },
        { replace: true }
      );
    }
  });

  let inBackgroundEditMode = Globals._uiConfig.InBackgroundEditorMode();

  const wsMgr = StateLib.GetWSMgr();
  const currentFilename = wsMgr?.GetFileName(true);
  const isBackground = currentFilename ? IsBackgroundFile(currentFilename) : false;
  let singleClickButton = inEditMode || isBackground;
  let menuOptions = singleClickButton ? [] : editMenuOptions();

  const isIsolvedBrand = gWhiteLabel === enumBrand.isolved;
  const showIsolvedUpgradeStyling = isIsolvedBrand && showCustomChartUpgradeGate;

  let onEditClick = null;
  if (inEditMode || (isBackground && inBackgroundEditMode)) onEditClick = returnToViewing;
  else if (isBackground) {
    onEditClick = editBackground;
  }
  const docMgr = wsMgr?.GetDocMgr();
  const triggerLabel = inEditMode ? appstr("Finish editing") : appstr("Edit");
  const triggerIconName = showIsolvedUpgradeStyling ? gUpgradeIndicatorIcon : "pen-to-square";
  const backgroundEditTriggerTestId = inEditMode
    ? "background-editor-toolbar-finish-editing-btn"
    : "background-editor-toolbar-edit-btn";
  const editTrigger = (
    <Button
      dataTestId={
        isBackground
          ? backgroundEditTriggerTestId
          : showIsolvedUpgradeStyling
            ? "edit-menu-upgrade-trigger-btn"
            : (editModeTestId("finish-editing-btn") ?? "edit-menu-trigger-btn")
      }
      isFullWidth={true}
      iconAlignEnd={false}
      iconOnly={false}
      className={showIsolvedUpgradeStyling ? "ocn-edit-menu-trigger--isolved-gated" : undefined}
      iconProps={{
        modifier: "white",
        name: triggerIconName,
        title: triggerIconName,
        dataTestId: showIsolvedUpgradeStyling ? "edit-menu-upgrade-icon" : undefined
      }}
      loading={docMgr?.fSaveQueued}
      disabled={chartDocument.state == null || docMgr?.fSaveQueued}
      onClick={onEditClick}
      modifier="secondary"
      type="button"
    >
      {triggerLabel}
    </Button>
  );

  return (
    <div className="ocn-edit-menu">
      {singleClickButton ? (
        editTrigger
      ) : (
        <Menu
          datatestId={"edit-custom-chart-menu"}
          fillWidth={true}
          trigger={editTrigger}
          disabled={chartDocument.state == null || showIsolvedUpgradeStyling}
          options={menuOptions}
          position={"bottom-end"}
          optionSize={"large"}
        />
      )}

      <CustomChartBuilderModal
        onClose={() => {
          setSearchParams(params => {
            params.delete("create");
            return params;
          });
        }}
        onCreate={data => {
          hasBuildStarted.current = true;
          createCustomChart(data);
        }}
        show={showCustomChartBuilderModal}
      />
    </div>
  );
};

export default EditMenu;
